import { useState } from "react";
import CloneFilesPanel from "../components/CloneFilesPanel";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  isCarePremise,
  type PmsConfig,
  type PremiseConfig,
} from "@shared/types/foyer";
import DemoIpRangesEditor from "../components/DemoIpRangesEditor";
import SupportedLocalesCard from "../components/SupportedLocalesCard";
import OpeningHoursCard from "../components/OpeningHoursCard";
import CommitDrawer from "../components/CommitDrawer";
import { PermissionGate } from "../auth/access";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import CallPolicyEditor from "../components/CallPolicyEditor";
import { PageHeader } from "../components/PageHeader";

interface Props {
  premise: PremiseConfig;
  /** Persist one change now and toast (PremiseWorkspace `commit`). Controls
   *  here either save the moment they change or save from their own drawer. */
  onCommit: PremiseCommit;
}

function emptyPms(): PmsConfig {
  return {
    vendor: null,
    dailyReset: false,
    scheduledCheckInOut: { enabled: false, schedule: {} },
    messagePowerOn: false,
    defaultScheme: null,
  };
}

export default function HospitalityConfigPage({ premise, onCommit }: Props) {
  const { t } = useTranslation("premises");
  const [editingName, setEditingName] = useState(false);
  return (
    <Stack spacing={3} data-testid="hospitality-config-page">
      <PageHeader title={t("hospitalityConfigPage.title")} />

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">
              {t("hospitalityConfigPage.nameAndId")}
            </Typography>
            <PermissionGate resource="premises" level="administrate">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setEditingName(true)}
                data-testid="premise-name-edit-open"
              >
                {t("hospitalityConfigPage.editName")}
              </Button>
            </PermissionGate>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" data-testid="premise-name-value">
              {premise.premiseName || t("hospitalityConfigPage.noDisplayName")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "fontFamilyMono" }}
            >
              {t("hospitalityConfigPage.premiseIdValue", {
                id: premise.premiseId,
              })}
            </Typography>
          </Stack>
        </CardContent>
        {editingName && (
          <NameDrawer
            premise={premise}
            onClose={() => setEditingName(false)}
            onCommit={onCommit}
          />
        )}
      </Card>

      {/*
        Location SETTINGS, not the dashboard.
        A premise owner logs in and lands on the dashboard; it is a read-at-a-
        glance surface, and burying a form somebody fills in once at setup
        inside it hides the form and clutters the glance. Opening hours belong
        with the other things about this place that are configured and then
        left alone.
      */}
      <PermissionGate resource="premises" level="administrate">
        <OpeningHoursCard premise={premise} onCommit={onCommit} />
      </PermissionGate>

      {/* The Hotel / Care / Hospital picker used to live here. A place is now
          just a place - what it IS follows from the products it has, so Care
          is switched on under Products and `segment` is derived from it. */}

      {isCarePremise(premise) && (
        <Box data-testid="video-calling-config">
          <Typography variant="h6" gutterBottom>
            {t("hospitalityConfigPage.videoCalls")}
          </Typography>
          <PermissionGate resource="video-calls" level="administrate">
            <CallPolicyEditor premise={premise} onCommit={onCommit} />
          </PermissionGate>
        </Box>
      )}

      {/* PMS-driven: the check-in message is what wakes the TV. Care premises
          have no PMS bound, so the switch would be a dead control there. */}
      {!isCarePremise(premise) && (
        <Card variant="outlined">
          <CardContent>
            <FormControlLabel
              control={
                <PermissionGate
                  resource="pms-link"
                  level="administrate"
                  deniedProps={{ disabled: true }}
                >
                  <Switch
                    checked={!!premise.pmsConfig?.messagePowerOn}
                    onChange={(e) => {
                      const on = e.target.checked;
                      void onCommit(
                        (c) => ({
                          ...c,
                          pmsConfig: {
                            ...(c.pmsConfig ?? emptyPms()),
                            messagePowerOn: on,
                          },
                        }),
                        {
                          success: on
                            ? t(
                                "hospitalityConfigPage.messagePowerOnTurnedOn",
                                {
                                  name: premise.premiseName,
                                }
                              )
                            : t(
                                "hospitalityConfigPage.messagePowerOnTurnedOff",
                                {
                                  name: premise.premiseName,
                                }
                              ),
                          failure: on
                            ? "turnOnMessagePowerOnFor"
                            : "turnOffMessagePowerOnFor",
                          failureParams: { name: premise.premiseName },
                        }
                      );
                    }}
                    inputProps={
                      { "data-testid": "cfg-message-power-on" } as Record<
                        string,
                        string
                      >
                    }
                  />
                </PermissionGate>
              }
              label={t("hospitalityConfigPage.enableMessagePowerOn")}
            />
          </CardContent>
        </Card>
      )}

      {/* TV-6's player choice ("External TV app" / "Built-in player") lives on
          the TV item it governs now: Start & navigation → Start view → TV. */}
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="cfg-tv-player-moved"
      >
        <Trans
          t={t}
          i18nKey="hospitalityConfigPage.tvPlayerMoved"
          components={{
            link: <Link component={RouterLink} to="/frontlayer/home?item=tv" />,
          }}
        />
      </Typography>

      <PermissionGate resource="tv-layout/translations" level="administrate">
        <SupportedLocalesCard premise={premise} onCommit={onCommit} />
      </PermissionGate>

      <PermissionGate resource="premises" level="administrate">
        <DemoIpRangesEditor premise={premise} onCommit={onCommit} />
      </PermissionGate>

      <CloneFilesPanel premiseId={premise.premiseId} />
    </Stack>
  );
}

function NameDrawer({
  premise,
  onClose,
  onCommit,
}: {
  premise: PremiseConfig;
  onClose: () => void;
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation("premises");
  const [name, setName] = useState(premise.premiseName);
  const trimmed = name.trim();
  return (
    <CommitDrawer
      title={t("hospitalityConfigPage.editName")}
      subtitle={premise.premiseId}
      onClose={onClose}
      saveDisabled={!trimmed || trimmed === premise.premiseName}
      data-testid="premise-name-drawer"
      onSave={() =>
        onCommit((c) => ({ ...c, premiseName: trimmed }), {
          success: t("hospitalityConfigPage.renamed", {
            from: premise.premiseName,
            to: trimmed,
          }),
          failure: "renamePremise",
          failureParams: { name: premise.premiseName },
        })
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t("hospitalityConfigPage.displayName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          placeholder={t("hospitalityConfigPage.displayNamePlaceholder")}
          inputProps={{ "data-testid": "premise-name-edit" }}
        />
        <TextField
          label={t("hospitalityConfigPage.premiseId")}
          value={premise.premiseId}
          disabled
          fullWidth
          size="small"
          helperText={t("hospitalityConfigPage.slugFixed")}
          sx={{
            "& .MuiInputBase-input": {
              fontFamily: "fontFamilyMono",
            },
          }}
        />
      </Stack>
    </CommitDrawer>
  );
}
